import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WebsiteRejections } from "@/components/website/WebsiteRejections";

const REASONS = {
    email_conflict: "Phone matches one person, but their email differs",
    multiple_phone_matches: "Phone matches more than one person",
    email_match_phone_conflict: "Email matches a person with a different phone",
    multiple_email_matches: "Email matches more than one person",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles" }) + " PT" : "—");

function Field({ label, value, testId }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-sm break-words" data-testid={testId}>{value || "—"}</div>
        </div>
    );
}

function ReviewCard({ item, onDone }) {
    const p = item.payload || {};
    const c = p.customer || {};
    const sid = item.submission_id;
    const [confirm, setConfirm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [manual, setManual] = useState({ record_type: "lead", record_id: "" });
    const run = async () => {
        setBusy(true);
        try {
            const { kind, body } = confirm;
            await api.post(`/website-reviews/${sid}/${kind}`, body || {});
            toast.success({ dismiss: "Submission dismissed", "create-lead": "New lead created", attach: "Submission attached" }[kind]);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); setConfirm(null); }
    };
    const ask = (kind, label, body) => setConfirm({ kind, label, body });
    return (
        <div className="rounded-xl border bg-white p-5 space-y-4" data-testid={`review-card-${sid}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="font-medium" data-testid={`review-name-${sid}`}>{c.name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "No name"}</div>
                    <div className="text-xs text-muted-foreground">Submitted {fmt(p.submitted_at)} · ID {sid.slice(0, 8)}</div>
                </div>
                <span className="rounded bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs text-amber-900" data-testid={`review-reason-${sid}`}>
                    {REASONS[item.review_reason] || item.review_reason}
                </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Phone" value={c.phone} testId={`review-phone-${sid}`} />
                <Field label="Email" value={c.email} testId={`review-email-${sid}`} />
                <Field label="Loan balance" value={p.loan_balance_range} />
                <Field label="Page" value={(p.attribution || {}).page} />
                <div className="col-span-2 md:col-span-4"><Field label="Message" value={p.message} /></div>
                <div className="col-span-2 md:col-span-4">
                    <Field label="Callback consent (callback request only, not marketing or SMS consent)"
                           value={Object.entries(p.consent || {}).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v === true ? "yes" : v === false ? "no" : v}`).join(" · ")} />
                </div>
            </div>
            <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Possible matches</div>
                <div className="space-y-2">
                    {(item.candidates || []).map((m) => (
                        <div key={m.record_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2" data-testid={`review-candidate-${m.record_id}`}>
                            <div className="text-sm">
                                <Link className="font-medium hover:underline" to={`/${m.record_type === "client" ? "clients" : "leads"}/${m.record_id}`}>{m.name || "Unnamed"}</Link>
                                <span className="ml-2 text-xs text-muted-foreground">{m.record_type} · {m.phone || "no phone"} · {m.email || "no email"}{m.case_id ? ` · case ${m.case_id}` : ""}</span>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => ask("attach", `Attach to ${m.name || m.record_id}`, { record_type: m.record_type, record_id: m.record_id })} data-testid={`review-attach-${m.record_id}`}>Attach here</Button>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
                <Select value={manual.record_type} onValueChange={(v) => setManual({ ...manual, record_type: v })}>
                    <SelectTrigger className="w-28" data-testid={`review-manual-type-${sid}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="lead">Lead</SelectItem><SelectItem value="client">Client</SelectItem></SelectContent>
                </Select>
                <Input className="w-64" placeholder="Other record ID" value={manual.record_id} onChange={(e) => setManual({ ...manual, record_id: e.target.value.trim() })} data-testid={`review-manual-id-${sid}`} />
                <Button size="sm" variant="outline" disabled={!manual.record_id} onClick={() => ask("attach", `Attach to ${manual.record_type} ${manual.record_id}`, manual)} data-testid={`review-manual-attach-${sid}`}>Attach by ID</Button>
                <Button size="sm" variant="outline" onClick={() => ask("create-lead", "Create a new unassigned lead")} data-testid={`review-create-${sid}`}>Create new lead</Button>
                <Button size="sm" variant="ghost" className="text-red-700" onClick={() => ask("dismiss", "Dismiss as spam (nothing is created)", { reason: "spam" })} data-testid={`review-dismiss-${sid}`}>Dismiss as spam</Button>
            </div>
            {confirm && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid={`review-confirm-${sid}`}>
                    <span>{confirm.label}? This can only be done once.</span>
                    <Button size="sm" onClick={run} disabled={busy} data-testid={`review-confirm-yes-${sid}`}>{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Confirm</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(null)} data-testid={`review-confirm-no-${sid}`}>Cancel</Button>
                </div>
            )}
        </div>
    );
}

export default function WebsiteReviews() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState(null);
    const [tick, setTick] = useState(0);
    const load = useCallback(async () => {
        setTick((t) => t + 1);
        try { setItems((await api.get("/website-reviews")).data.items); }
        catch (e) { toast.error(formatApiError(e)); setItems([]); }
    }, []);
    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
    if (user && !isAdmin) return <Navigate to="/" replace />;
    return (
        <div className="space-y-6" data-testid="website-reviews-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Website reviews</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        A+ Website callback requests that couldn't be matched to one person safely. Nothing is attached, created or sent until you choose.
                    </p>
                </div>
                <Button variant="outline" onClick={load} data-testid="website-reviews-refresh"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
            </div>
            {items === null && <Loader2 className="h-5 w-5 animate-spin" />}
            {items && items.length === 0 && <div className="text-sm text-muted-foreground" data-testid="website-reviews-empty">No submissions waiting for review.</div>}
            {items && items.map((it) => <ReviewCard key={it.submission_id} item={it} onDone={load} />)}
            {isAdmin && <div className="pt-6 border-t"><WebsiteRejections key={tick} /></div>}
        </div>
    );
}
